// Signage → Resumen (/signage/dashboard). Estado de pantallas (online/offline,
// qué muestran ahora) + proof-of-play: qué contenido se reprodujo, cuánto y en
// qué pantallas, con selector de período.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, LayoutDashboard, MonitorCheck, PlayCircle, Package, ScanLine, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { sigRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface ValidacionPrecios { ok: boolean; combos_revisados: number; discrepancias: { combo: string; problema: string }[] }

interface Estado { escena?: string; titulo?: string | null }
interface PantallaEstado { id: string; nombre: string; codigo: string | null; local: string | null; grupo: string | null; plantilla: string | null; online: boolean; last_seen: string | null; version: string | null; estado: Estado | null }
interface Dash { total: number; online: number; pantallas: PantallaEstado[] }
interface Pop {
  dias: number; total_repro: number; total_seg: number; productos_distintos: number;
  escaneos: number; escaneos_por_pantalla: { pantalla: string; n: number }[]; escaneos_top: { slug: string; n: number }[];
  por_tipo: { tipo: string; n: number; seg: number }[];
  top: { titulo: string; tipo: string; n: number; seg: number }[];
  por_pantalla: { pantalla: string; n: number; seg: number }[];
  por_dia: { dia: string; n: number }[];
}

const fmtSeg = (s: number) => { if (s < 60) return `${s}s`; const m = Math.round(s / 60); if (m < 60) return `${m} min`; const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; };
const TIPO_L: Record<string, string> = { catalogo: 'Catálogo', carta: 'Carta', mensaje: 'Mensaje', banner: 'Banner', qr: 'QR' };

function Kpi({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Card><CardContent className="p-4 flex items-center gap-3">
      <div className="text-primary">{icon}</div>
      <div><div className="text-2xl font-bold tabular-nums">{value}</div><div className="text-xs text-muted-foreground">{label}{sub ? ` · ${sub}` : ''}</div></div>
    </CardContent></Card>
  );
}

export function DashboardSignage() {
  const [dias, setDias] = useState(7);

  const validarPrecios = async () => {
    try {
      const r = await sigRpc<ValidacionPrecios>('sig_validar_precios');
      if (r.ok) toast.success(`Coherencia pantalla↔caja OK — ${r.combos_revisados} combo(s) revisados`);
      else toast.error(
        `${r.discrepancias.length} discrepancia(s): ` + r.discrepancias.map((d) => `${d.combo}: ${d.problema}`).join(' · '),
        { duration: 12000 },
      );
    } catch (e) { toast.error(e instanceof Error ? e.message : 'No se pudo validar'); }
  };
  const { data: dash } = useQuery<Dash>({ queryKey: ['sig-dash'], queryFn: () => sigRpc<Dash>('sig_dashboard'), staleTime: 0, refetchInterval: 30000 });
  const { data: pop } = useQuery<Pop>({ queryKey: ['sig-pop', dias], queryFn: () => sigRpc<Pop>('sig_pop', { _dias: dias }), staleTime: 0, refetchInterval: 60000 });

  const maxTop = Math.max(1, ...(pop?.top ?? []).map((t) => t.n));
  const maxDia = Math.max(1, ...(pop?.por_dia ?? []).map((d) => d.n));

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><LayoutDashboard className="w-5 h-5" /> Signage — Resumen</h1>
        <button
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          title="Verifica que lo que las pantallas prometen (combos, precios) coincida con lo que cobra la caja"
          onClick={() => void validarPrecios()}
        >
          <ShieldCheck className="w-4 h-4" /> Validar precios pantalla↔caja
        </button>
        <div className="inline-flex rounded-full border overflow-hidden text-sm">
          {[7, 30].map((d) => (
            <button key={d} className={`px-3 py-1.5 ${dias === d ? 'bg-foreground text-background font-medium' : 'text-muted-foreground'}`} onClick={() => setDias(d)}>{d} días</button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi icon={<MonitorCheck className="w-7 h-7" />} label="Pantallas en línea" value={`${dash?.online ?? 0}/${dash?.total ?? 0}`} />
        <Kpi icon={<PlayCircle className="w-7 h-7" />} label="Reproducciones" value={String(pop?.total_repro ?? 0)} sub={`${dias} días`} />
        <Kpi icon={<Clock className="w-7 h-7" />} label="Tiempo emitido" value={fmtSeg(pop?.total_seg ?? 0)} />
        <Kpi icon={<Package className="w-7 h-7" />} label="Productos distintos" value={String(pop?.productos_distintos ?? 0)} />
        <Kpi icon={<ScanLine className="w-7 h-7" />} label="Escaneos QR" value={String(pop?.escaneos ?? 0)}
          sub={(pop?.escaneos_por_pantalla ?? [])[0] ? `top: ${pop!.escaneos_por_pantalla[0].pantalla}` : undefined} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Estado de las pantallas</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground border-b"><th className="py-1.5 pr-3 text-left">Pantalla</th><th className="py-1.5 pr-3 text-left">Mostrando</th><th className="py-1.5 text-left">Estado</th></tr></thead>
              <tbody>
                {(dash?.pantallas ?? []).map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">{p.nombre}<div className="text-xs text-muted-foreground">{p.local ?? '—'}</div></td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{p.estado?.titulo || (p.estado?.escena ? TIPO_L[p.estado.escena] : '—')}</td>
                    <td className="py-1.5">
                      <span className={`inline-flex items-center gap-1.5 ${p.online ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <span className={`w-2 h-2 rounded-full ${p.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                        {p.online ? 'En línea' : p.last_seen ? new Date(p.last_seen).toLocaleString(LOCALE) : 'Nunca'}
                      </span>
                    </td>
                  </tr>
                ))}
                {!(dash?.pantallas ?? []).length && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin pantallas registradas.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Contenido más mostrado</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(pop?.top ?? []).slice(0, 12).map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="w-40 truncate" title={t.titulo}>{t.titulo}</span>
                <div className="flex-1 h-4 bg-muted rounded overflow-hidden"><div className="h-full bg-primary/70" style={{ width: `${(t.n / maxTop) * 100}%` }} /></div>
                <span className="w-20 text-right tabular-nums text-muted-foreground">{t.n} · {fmtSeg(t.seg)}</span>
              </div>
            ))}
            {!(pop?.top ?? []).length && <div className="py-6 text-center text-muted-foreground text-sm">Aún no hay reproducciones registradas. Abre el reproductor de una pantalla para empezar a medir.</div>}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Reproducciones por día</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1.5 h-28">
              {(pop?.por_dia ?? []).map((d) => (
                <div key={d.dia} className="flex-1 flex flex-col items-center gap-1" title={`${d.dia}: ${d.n}`}>
                  <div className="w-full bg-primary/60 rounded-t" style={{ height: `${(d.n / maxDia) * 100}%`, minHeight: d.n ? '3px' : '0' }} />
                  <span className="text-[10px] text-muted-foreground">{new Date(d.dia).toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit' })}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por tipo de escena · por pantalla</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Escenas</div>
              {(pop?.por_tipo ?? []).map((t) => <div key={t.tipo} className="flex justify-between py-0.5"><span>{TIPO_L[t.tipo] ?? t.tipo}</span><span className="tabular-nums text-muted-foreground">{t.n}</span></div>)}
              {!(pop?.por_tipo ?? []).length && <div className="text-muted-foreground">—</div>}
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Pantallas</div>
              {(pop?.por_pantalla ?? []).map((p) => <div key={p.pantalla} className="flex justify-between py-0.5"><span className="truncate mr-2">{p.pantalla}</span><span className="tabular-nums text-muted-foreground">{p.n}</span></div>)}
              {!(pop?.por_pantalla ?? []).length && <div className="text-muted-foreground">—</div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
